import type { StateCreator } from 'zustand'

// As Vistas 2D deixaram de ser uma janela flutuante e passaram a ser um
// MODO da plataforma: a área central do viewer mostra o 3D ou o desenho
// 2D, e o comutador alterna entre os dois. O store só guarda QUAL a
// superfície activa; tudo o resto (lista de vistas gravadas, desenho
// gerado, pré-visualização) continua a viver dentro da superfície ou no
// localStorage por modelo: não há razão para pôr desenhos de dezenas de
// milhares de pontos no store do Zustand.
export type ViewerSurface = '3d' | '2d'

const SURFACE_KEY = 'vn2r.viewerSurface'

/** Lê a superfície persistida. Qualquer coisa que não seja '2d' (chave
 *  ausente, valor estragado, localStorage bloqueado) dá 3D: o modo por
 *  omissão da aplicação. */
export function loadViewerSurface(): ViewerSurface {
  try {
    return localStorage.getItem(SURFACE_KEY) === '2d' ? '2d' : '3d'
  } catch {
    return '3d'
  }
}

export function persistViewerSurface(surface: ViewerSurface): void {
  try {
    localStorage.setItem(SURFACE_KEY, surface)
  } catch {
    /* modo privado ou quota cheia: a preferência não sobrevive ao
       reload, mas a sessão corrente funciona na mesma */
  }
}

export interface Views2DSlice {
  viewerSurface: ViewerSurface
  setViewerSurface: (surface: ViewerSurface) => void
  toggleViewerSurface: () => void
  /** O 2D está a viver numa janela do browser à parte (dois ecrãs: o
   *  modelo num, a planta no outro). Enquanto isto for verdade a área
   *  central da janela principal fica no 3D. */
  views2dDetached: boolean
  setViews2DDetached: (detached: boolean) => void
}

/** Entrar no modo 2D com ele destacado seria mostrar o mesmo desenho em
 *  dois sítios, por isso pôr a superfície em 2D reanexa sempre a janela
 *  externa. É isto que faz o botão "2D" do comutador (e o do ribbon)
 *  servir de "trazer de volta" sem lógica própria. */
function surfacePatch(surface: ViewerSurface) {
  return surface === '2d'
    ? { viewerSurface: surface, views2dDetached: false }
    : { viewerSurface: surface }
}

export const createViews2DSlice: StateCreator<Views2DSlice> = (set, get) => ({
  viewerSurface: loadViewerSurface(),
  // Não se persiste: reabrir a janela sozinha no arranque da página seria
  // um popup sem clique nenhum atrás, e todos os browsers o bloqueiam.
  views2dDetached: false,
  setViewerSurface: (surface) => {
    persistViewerSurface(surface)
    set(surfacePatch(surface))
  },
  toggleViewerSurface: () => {
    const next: ViewerSurface = get().viewerSurface === '2d' ? '3d' : '2d'
    persistViewerSurface(next)
    set(surfacePatch(next))
  },
  setViews2DDetached: (detached) => set({ views2dDetached: detached }),
})
